import { useCallback, useMemo } from 'react';

import { useCreateScope } from '../../../modules/Apps/Scopes';
import { getApiErrorMessage } from '../../../utils/Api';
import { PopoverStateReturn } from '../../common/Drawer/Drawer.context';
import { FormDrawer } from '../../common/FormDrawer';
import { useToasterContext } from '../../common/Toaster';

import {
  createScopesFormValidationSchema,
  ScopesForm,
  ScopesFormValues,
  ScopesFormValuesToDto,
} from './ScopesForm';

interface CreateScopesDrawerProps {
  state: PopoverStateReturn;
  appId: string;
}

export function CreateScopesDrawer({ state, appId }: CreateScopesDrawerProps) {
  const { mutateAsync } = useCreateScope();
  const { showSuccess, showError } = useToasterContext();

  const handleSubmit = useCallback(
    async (values: ScopesFormValues) => {
      await mutateAsync(ScopesFormValuesToDto(values), {
        onSuccess: () => showSuccess('Scope successfully created.'),
        onError: error =>
          showError(
            getApiErrorMessage(
              error,
              'There was an error creating the Scope. Please, try again later.',
            ),
          ),
      });
    },
    [mutateAsync, showError, showSuccess],
  );

  const initialValues: ScopesFormValues = useMemo(
    () => ({
      appId,
      byPassOptIn: false,
      doubleOptIn: false,
      doubleOptInAcceptOptions: '',
      doubleOptInMsg: '',
      doubleOptInRejectOptions: '',
      keyword: '',
      name: '',
    }),
    [appId],
  );

  const validationSchema = useMemo(() => createScopesFormValidationSchema(), []);

  return (
    <FormDrawer
      state={state}
      handleSubmit={handleSubmit}
      initialValues={initialValues}
      title="Add Scope"
      subtitle="Create a new scope for the application"
      validationSchema={validationSchema}
      validateOnBlur={false}
    >
      {() => <ScopesForm />}
    </FormDrawer>
  );
}
